import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { firstRunCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Avatar } from '@/ui/Avatar/Avatar'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { StatusDot } from '@/ui/Status/Status'
import { RefreshFailed } from '../components/RefreshFailed'
import type { Crumb } from '../components/WsPageShell'
import { WsPageShell } from '../components/WsPageShell'
import { ROUTES } from '../constants/routes'
import { useWaitingQuery } from '../hooks/queries'
import { useNow } from '../hooks/useNow'
import { useWorkspaceStore } from '../store/workspace'
import { formatRelative } from '../utils/time'
import styles from './WaitingPage.module.css'

/**
 * Workspace-wide "waiting on you" inbox (redesign §2): every unresolved
 * agent review item (submitted plan / unanswered question) across all
 * topics, oldest first. It is a filter, not a place — resolving in the
 * thread clears the row (the waiting list refetches on review-decision
 * events; see ws/eventRouter).
 */
export function WaitingPage() {
  const { workspaceId = '' } = useParams()
  const now = useNow()
  const workspaces = useWorkspaceStore((s) => s.workspaces)
  const workspaceName = workspaces.find(
    (w) => w.workspace_id === workspaceId,
  )?.name
  const {
    data: waiting,
    isLoading,
    isError,
    refetch,
  } = useWaitingQuery(workspaceId)
  const showSkeleton = useSkeletonDelay(isLoading)

  const crumbs: Crumb[] = [
    { label: workspaceName ?? firstRunCopy.workspaceFallback, to: ROUTES.home },
    { label: firstRunCopy.waiting.title },
  ]
  const items = waiting?.items ?? []

  let body: ReactNode
  if (isLoading) {
    body = showSkeleton ? (
      <Stack gap="space-12">
        {[0, 1, 2].map((i) => (
          <Card key={i}>
            <Skeleton lines={2} ground="surface" />
          </Card>
        ))}
      </Stack>
    ) : null
  } else if (isError && !waiting) {
    body = (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline">
          {firstRunCopy.waiting.loadFailed}
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {firstRunCopy.retry}
          </Button>
        </Inline>
      </Alert>
    )
  } else if (items.length === 0) {
    body = (
      <EmptyState variant="inline" description={firstRunCopy.waiting.empty} />
    )
  } else {
    body = (
      <Stack gap="space-12" className={styles.list}>
        {items.map((item) => {
          const action =
            item.kind === 'plan'
              ? firstRunCopy.waiting.action.plan
              : firstRunCopy.waiting.action.question
          const agentLabel =
            item.agent_name ??
            item.agent_id ??
            firstRunCopy.waiting.agentFallback
          const age = formatRelative(item.submitted_at, now)
          return (
            <Card
              as="a"
              key={item.resource_id}
              href={
                item.thread_id
                  ? ROUTES.topicThread(item.topic_id, item.thread_id)
                  : ROUTES.topic(item.topic_id)
              }
            >
              <Inline gap="space-8" align="start" wrap={false}>
                <Avatar name={agentLabel} kind="agent" size="md" />
                <Inline
                  gap="space-8"
                  align="baseline"
                  className={`${styles.head} ${styles.grow}`}
                >
                  <Text weight="strong" tone="text" className={styles.grow}>
                    {item.name}
                  </Text>
                  <Text variant="caption" tone="text-mute" numeric>
                    {item.topic_name} · {age}
                  </Text>
                </Inline>
              </Inline>
              <Inline gap="space-8" align="baseline">
                <Text tone="text-sub">
                  <StatusDot tone="warning" label={action} />
                </Text>
                <Text variant="caption" tone="text-mute">
                  {item.thread_id && item.thread_title
                    ? firstRunCopy.waiting.blocksThread(
                        agentLabel,
                        item.thread_title,
                      )
                    : firstRunCopy.waiting.blocks(agentLabel)}
                </Text>
              </Inline>
            </Card>
          )
        })}
      </Stack>
    )
  }

  return (
    <WsPageShell
      crumbs={crumbs}
      title={firstRunCopy.waiting.title}
      description={firstRunCopy.waiting.description}
    >
      <div aria-busy={isLoading || undefined}>
        {isError && waiting && (
          <RefreshFailed
            message={firstRunCopy.waiting.loadFailed}
            onRetry={() => void refetch()}
          />
        )}
        {body}
      </div>
    </WsPageShell>
  )
}
